import { t as uiText } from '../../i18n';
// src/features/workspace/updatePublishId.ts
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface UpdatePublishIdParams {
  workspaceId: string;
  publishId: string;
}

interface UpdatePublishIdResult {
  success: boolean;
  error?: string;
}

/**
 * publishIDのクライアント側バリデーション（重複チェックなし）
 */
export const validatePublishIdFormat = (publishId: string): { valid: boolean; error?: string } => {
  // 英数字とハイフンのみ許可、3-50文字
  const publishIdRegex = /^[a-zA-Z0-9-]{3,50}$/;
  if (!publishIdRegex.test(publishId)) {
    return {
      valid: false,
      error: uiText("publishID may contain only letters, numbers, and hyphens (-), and must be 3-50 characters")
    };
  }

  // 予約語チェック
  const reservedWords = ['admin', 'api', 'www', 'mail', 'ftp', 'localhost', 'test', 'staging', 'dev', 'demo'];
  if (reservedWords.includes(publishId.toLowerCase())) {
    return {
      valid: false,
      error: uiText("This publishID cannot be used")
    };
  }

  return { valid: true };
};

/**
 * publishIDの重複チェック（サーバー側で実行）
 * 注意: この関数は権限の問題により、実際の重複チェックはupdatePublishId内で行われます
 */
export const checkPublishIdAvailability = async (publishId: string, currentWorkspaceId?: string): Promise<{ available: boolean; error?: string }> => {
  // クライアント側では形式チェックのみ
  const formatCheck = validatePublishIdFormat(publishId);
  if (!formatCheck.valid) {
    return {
      available: false,
      error: formatCheck.error
    };
  }

  // 重複チェックはサーバー側（updatePublishId）で行う
  return { available: true };
};

/**
 * ワークスペースのpublishIDを更新
 */
export const updatePublishId = async ({
  workspaceId,
  publishId
}: UpdatePublishIdParams): Promise<UpdatePublishIdResult> => {
  try {
    const formatCheck = validatePublishIdFormat(publishId);
    if (!formatCheck.valid) {
      return {
        success: false,
        error: formatCheck.error
      };
    }
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    const workspaceDoc = await getDoc(workspaceRef);

    if (!workspaceDoc.exists()) {
      return {
        success: false,
        error: uiText("Workspace not found")
      };
    }
    await updateDoc(workspaceRef, {
      publishId: publishId,
      updatedAt: new Date()
    });

    return { success: true };
  } catch (error) {
    console.error('🔄 updatePublishId: エラーが発生しました:', error);
    
    // Firestoreエラーの詳細を確認
    if (error && typeof error === 'object' && 'code' in error) {
      const firestoreError = error as { code: string; message: string };
      if (firestoreError.code === 'permission-denied') {
        return {
          success: false,
          error: uiText("You do not have permission to update the publishID. You must be a Workspace admin.")
        };
      }
    }
    
    return {
      success: false,
      error: uiText("Failed to update publishID")
    };
  }
};
